import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Activity, TrendingUp, TrendingDown, AlertTriangle, Info, Zap,
  RefreshCw, Loader, Search, ChevronDown, ChevronUp, Target,
  Shield, Flame, BarChart3, Layers, Brain, HelpCircle, X,
  ArrowUpRight, ArrowDownRight, Minus, Clock, Calendar, BookOpen,
  ChevronRight, Lightbulb, GraduationCap, Eye, MousePointer
} from 'lucide-react';

// ============================================
// MOBILE-FRIENDLY GAMMA EXPOSURE DASHBOARD
// ============================================

const GammaExposureDashboard = ({ symbol: initialSymbol }) => {
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol || 'SPY');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const [gexData, setGexData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEducation, setShowEducation] = useState(false);
  const [educationTab, setEducationTab] = useState('basics');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  // Chart hover state
  const [hoveredBar, setHoveredBar] = useState(null);
  const chartRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (currentSymbol) {
      fetchGEXData();
    }
  }, [currentSymbol]);

  const fetchGEXData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/gamma/exposure/${currentSymbol}`);
      const data = await response.json();
      
      if (data.success) {
        setGexData(data);
        generateAIAnalysis(data);
      } else {
        setError(data.error || 'Failed to fetch GEX data');
      }
    } catch (err) {
      console.error('GEX fetch error:', err);
      setError('Failed to load gamma exposure data');
    } finally {
      setLoading(false);
    }
  };

  const generateAIAnalysis = async (data) => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/gamma/ai-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: currentSymbol, gexData: data })
      });
      const analysis = await response.json();
      setAiAnalysis(analysis);
    } catch (err) {
      console.error('AI analysis error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const searchStocks = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data || []);
      setShowSearchResults(true);
    } catch (err) {
      setSearchResults([]);
    }
  };

  const selectStock = (symbol) => {
    setCurrentSymbol(symbol);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Calculate derived metrics
  const metrics = useMemo(() => {
    if (!gexData) return null;
    
    const { totalGEX, callGEX, putGEX, flipLevel, currentPrice, strikes } = gexData;
    
    const netGEX = callGEX - Math.abs(putGEX);
    const gexRatio = Math.abs(putGEX) > 0 ? callGEX / Math.abs(putGEX) : 0;
    const distanceToFlip = flipLevel ? ((currentPrice - flipLevel) / currentPrice * 100) : 0;
    const isPositiveGamma = currentPrice > flipLevel;
    
    const sortedStrikes = [...(strikes || [])].sort((a, b) => Math.abs(b.gex) - Math.abs(a.gex));
    const topCallWall = sortedStrikes.find(s => s.gex > 0);
    const topPutWall = sortedStrikes.find(s => s.gex < 0);
    
    return {
      netGEX,
      gexRatio,
      distanceToFlip,
      isPositiveGamma,
      topCallWall,
      topPutWall,
      volatilityRegime: isPositiveGamma ? 'LOW' : 'HIGH'
    };
  }, [gexData]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveTooltip(null);
    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeTooltip]);

  if (loading && !gexData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: isMobile ? '2rem 1rem' : '4rem',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
        borderRadius: '1rem',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} color="#a78bfa" className="spin" style={{ marginBottom: '1rem' }} />
          <p style={{ color: '#a78bfa' }}>Loading Gamma Exposure Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      borderRadius: isMobile ? '0.75rem' : '1rem',
      overflow: 'hidden',
      border: '1px solid rgba(139, 92, 246, 0.2)'
    }}>
      {/* Header - Responsive */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
        borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
        padding: isMobile ? '1rem' : '1.5rem 2rem'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'start', 
          gap: '1rem' 
        }}>
          <div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.75rem', 
              marginBottom: '0.5rem' 
            }}>
              <div style={{
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                flexShrink: 0
              }}>
                <Layers size={isMobile ? 22 : 28} color="white" />
              </div>
              <div>
                <h2 style={{ 
                  fontSize: isMobile ? '1.25rem' : '1.75rem', 
                  fontWeight: '700', 
                  margin: 0,
                  background: 'linear-gradient(135deg, #fff 0%, #a78bfa 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Gamma Exposure
                </h2>
                <p style={{ color: '#94a3b8', fontSize: isMobile ? '0.75rem' : '0.875rem', margin: 0 }}>
                  Market Maker Positioning
                </p>
              </div>
            </div>
            
            {/* Live Data Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
              padding: '0.375rem 0.75rem',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '2rem',
              fontSize: '0.65rem',
              fontWeight: '600',
              color: '#10b981'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 8px #10b981'
              }} />
              LIVE DATA
            </div>
          </div>

          {/* Action Buttons - Stack on mobile */}
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setShowEducation(!showEducation)}
              style={{
                padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                background: showEducation ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${showEducation ? 'rgba(251, 191, 36, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '0.5rem',
                color: showEducation ? '#fbbf24' : '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: isMobile ? '0.75rem' : '0.8rem',
                fontWeight: '600',
                transition: 'all 0.2s',
                flex: isMobile ? '1' : 'none'
              }}
            >
              <GraduationCap size={16} />
              {isMobile ? 'Learn' : 'Learn GEX'}
            </button>
            
            <button
              onClick={fetchGEXData}
              disabled={loading}
              style={{
                padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '0.5rem',
                color: '#a78bfa',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: isMobile ? '0.75rem' : '0.8rem',
                fontWeight: '600',
                flex: isMobile ? '1' : 'none'
              }}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Search - Full width on mobile */}
        <div style={{ 
          marginTop: '1rem', 
          position: 'relative', 
          maxWidth: isMobile ? '100%' : '400px' 
        }}>
          <Search size={18} style={{ 
            position: 'absolute', 
            left: '1rem', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: '#64748b' 
          }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchStocks(e.target.value);
            }}
            onFocus={() => setShowSearchResults(true)}
            placeholder="Search symbol..."
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(100, 116, 139, 0.3)',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '0.9rem',
              outline: 'none'
            }}
          />
          
          {showSearchResults && searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.5rem',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 100
            }}>
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => selectStock(result.symbol)}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    borderBottom: idx < searchResults.length - 1 ? '1px solid #334155' : 'none'
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#60a5fa' }}>{result.symbol}</div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{result.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Education Panel - Comprehensive */}
      {showEducation && (
        <EducationPanel 
          onClose={() => setShowEducation(false)} 
          activeTab={educationTab}
          setActiveTab={setEducationTab}
          isMobile={isMobile}
        />
      )}

      {/* Main Content */}
      {gexData && metrics && (
        <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
          {/* Current Symbol Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              borderRadius: '0.5rem'
            }}>
              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#60a5fa' }}>
                {currentSymbol}
              </span>
              <span style={{ marginLeft: '0.75rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                ${gexData.currentPrice?.toFixed(2)}
              </span>
            </div>
            
            {/* Gamma Environment Badge */}
            <div style={{
              padding: '0.5rem 1rem',
              background: metrics.isPositiveGamma 
                ? 'rgba(16, 185, 129, 0.15)' 
                : 'rgba(239, 68, 68, 0.15)',
              border: `1px solid ${metrics.isPositiveGamma ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {metrics.isPositiveGamma ? <Shield size={16} color="#10b981" /> : <Flame size={16} color="#ef4444" />}
              <span style={{ 
                color: metrics.isPositiveGamma ? '#10b981' : '#ef4444',
                fontWeight: '600',
                fontSize: '0.85rem'
              }}>
                {metrics.isPositiveGamma ? 'Positive Gamma' : 'Negative Gamma'}
              </span>
              <InfoTooltip 
                content={metrics.isPositiveGamma 
                  ? "Price is ABOVE the gamma flip level. Market makers hedge AGAINST price moves, dampening volatility. Expect mean-reversion and range-bound behavior."
                  : "Price is BELOW the gamma flip level. Market makers hedge WITH price moves, amplifying volatility. Expect trending/momentum behavior."
                }
                isMobile={isMobile}
              />
            </div>
          </div>

          {/* Metrics Grid - Responsive */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile 
              ? 'repeat(2, 1fr)' 
              : 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: isMobile ? '0.75rem' : '1rem',
            marginBottom: '1.5rem'
          }}>
            <MetricCard
              label="Net GEX"
              value={formatGEX(metrics.netGEX)}
              icon={<Activity size={18} />}
              color={metrics.netGEX > 0 ? '#10b981' : '#ef4444'}
              tooltip="Total gamma exposure across all strikes. Positive = more call gamma (bullish), Negative = more put gamma (bearish)"
              isMobile={isMobile}
            />
            
            <MetricCard
              label="Gamma Flip"
              value={`$${gexData.flipLevel?.toFixed(2) || 'N/A'}`}
              icon={<Zap size={18} />}
              color="#f59e0b"
              badge={`${Math.abs(metrics.distanceToFlip).toFixed(1)}% away`}
              tooltip="The High Volatility Level (HVL). Above this = stable/mean-reverting. Below this = volatile/trending. Critical level to watch!"
              highlight
              isMobile={isMobile}
            />
            
            <MetricCard
              label="Call/Put Ratio"
              value={metrics.gexRatio.toFixed(2)}
              icon={<BarChart3 size={18} />}
              color={metrics.gexRatio > 1 ? '#10b981' : metrics.gexRatio < 0.5 ? '#ef4444' : '#f59e0b'}
              tooltip="Call GEX divided by Put GEX. Above 1 = bullish positioning. Below 1 = bearish positioning."
              isMobile={isMobile}
            />
            
            <MetricCard
              label="Volatility Regime"
              value={metrics.volatilityRegime}
              icon={metrics.volatilityRegime === 'LOW' ? <Shield size={18} /> : <Flame size={18} />}
              color={metrics.volatilityRegime === 'LOW' ? '#10b981' : '#ef4444'}
              tooltip={metrics.volatilityRegime === 'LOW' 
                ? "Low volatility expected. Good for selling premium (iron condors, credit spreads)."
                : "High volatility expected. Good for buying premium (straddles, strangles) or momentum trades."
              }
              isMobile={isMobile}
            />
          </div>

          {/* Gamma Walls Section - Stack on mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <WallCard
              type="call"
              wall={metrics.topCallWall}
              currentPrice={gexData.currentPrice}
              allWalls={gexData.strikes?.filter(s => s.gex > 0).sort((a, b) => b.gex - a.gex).slice(0, 5)}
              isMobile={isMobile}
            />
            
            <WallCard
              type="put"
              wall={metrics.topPutWall}
              currentPrice={gexData.currentPrice}
              allWalls={gexData.strikes?.filter(s => s.gex < 0).sort((a, b) => a.gex - b.gex).slice(0, 5)}
              isMobile={isMobile}
            />
          </div>

          {/* GEX Profile Chart - Interactive */}
          <GEXProfileChart
            strikes={gexData.strikes}
            currentPrice={gexData.currentPrice}
            flipLevel={gexData.flipLevel}
            isMobile={isMobile}
            hoveredBar={hoveredBar}
            setHoveredBar={setHoveredBar}
          />

          {/* AI Analysis Section */}
          <AIAnalysisSection
            analysis={aiAnalysis}
            loading={aiLoading}
            symbol={currentSymbol}
            metrics={metrics}
            gexData={gexData}
            isMobile={isMobile}
          />

          {/* Trading Implications */}
          <TradingImplications
            metrics={metrics}
            gexData={gexData}
            isMobile={isMobile}
          />
        </div>
      )}

      {error && (
        <div style={{
          margin: isMobile ? '1rem' : '2rem',
          padding: isMobile ? '1.5rem' : '2rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.75rem',
          textAlign: 'center'
        }}>
          <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
          <button
            onClick={fetchGEXData}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: '#ef4444',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================
// INFO TOOLTIP - Touch-friendly
// ============================================
const InfoTooltip = ({ content, isMobile }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShow(false);
      }
    };
    if (show) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [show]);

  return (
    <div 
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex' }}
      onClick={(e) => {
        e.stopPropagation();
        setShow(!show);
      }}
      onMouseEnter={() => !isMobile && setShow(true)}
      onMouseLeave={() => !isMobile && setShow(false)}
    >
      <Info 
        size={14} 
        color="#64748b" 
        style={{ cursor: 'pointer' }} 
      />
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '0.5rem',
          padding: '0.75rem',
          background: '#1e293b',
          border: '1px solid #475569',
          borderRadius: '0.5rem',
          fontSize: '0.75rem',
          color: '#cbd5e1',
          width: isMobile ? '200px' : '250px',
          zIndex: 100,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
          lineHeight: '1.5'
        }}>
          {content}
          <div style={{
            position: 'absolute',
            bottom: '-6px',
            left: '50%',
            transform: 'translateX(-50%) rotate(45deg)',
            width: '12px',
            height: '12px',
            background: '#1e293b',
            borderRight: '1px solid #475569',
            borderBottom: '1px solid #475569'
          }} />
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPREHENSIVE EDUCATION PANEL
// ============================================
const EducationPanel = ({ onClose, activeTab, setActiveTab, isMobile }) => {
  const tabs = [
    { id: 'basics', label: 'Basics', icon: <BookOpen size={14} /> },
    { id: 'levels', label: 'Key Levels', icon: <Target size={14} /> },
    { id: 'trading', label: 'Trading', icon: <TrendingUp size={14} /> },
    { id: 'glossary', label: 'Glossary', icon: <GraduationCap size={14} /> }
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.05) 0%, rgba(245, 158, 11, 0.05) 100%)',
      borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
      padding: isMobile ? '1rem' : '1.5rem 2rem'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1rem' 
      }}>
        <h3 style={{ 
          fontSize: isMobile ? '1rem' : '1.125rem', 
          fontWeight: '700', 
          color: '#fbbf24',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Brain size={20} />
          Understanding GEX
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '0.25rem'
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        overflowX: 'auto',
        paddingBottom: '0.5rem'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.5rem 1rem',
              background: activeTab === tab.id 
                ? 'rgba(251, 191, 36, 0.2)' 
                : 'rgba(15, 23, 42, 0.5)',
              border: `1px solid ${activeTab === tab.id ? 'rgba(251, 191, 36, 0.4)' : 'rgba(100, 116, 139, 0.2)'}`,
              borderRadius: '0.5rem',
              color: activeTab === tab.id ? '#fbbf24' : '#94a3b8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        background: 'rgba(15, 23, 42, 0.5)',
        border: '1px solid rgba(100, 116, 139, 0.2)',
        borderRadius: '0.75rem',
        padding: isMobile ? '1rem' : '1.5rem'
      }}>
        {activeTab === 'basics' && <BasicsTab isMobile={isMobile} />}
        {activeTab === 'levels' && <LevelsTab isMobile={isMobile} />}
        {activeTab === 'trading' && <TradingTab isMobile={isMobile} />}
        {activeTab === 'glossary' && <GlossaryTab isMobile={isMobile} />}
      </div>
    </div>
  );
};

const BasicsTab = ({ isMobile }) => (
  <div>
    <h4 style={{ margin: '0 0 1rem 0', color: '#e2e8f0', fontSize: '1rem' }}>
      What is Gamma Exposure (GEX)?
    </h4>
    
    <div style={{ 
      background: 'rgba(59, 130, 246, 0.1)', 
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '0.5rem',
      padding: '1rem',
      marginBottom: '1rem'
    }}>
      <p style={{ margin: 0, color: '#93c5fd', fontSize: '0.9rem', lineHeight: '1.6' }}>
        <strong>GEX measures how much market makers must buy or sell shares</strong> to stay hedged 
        when the stock price moves. It reveals where natural "walls" of support and resistance exist 
        based on options positioning.
      </p>
    </div>

    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '1rem',
      marginBottom: '1rem'
    }}>
      <EducationCard
        title="Why Does It Matter?"
        icon="🎯"
        content="When market makers hedge, they create real buying/selling pressure. A strike with $1B of call gamma means MMs will sell ~$1B of stock as price approaches that level, creating resistance."
      />
      
      <EducationCard
        title="How Is It Calculated?"
        icon="📊"
        content="GEX = Gamma × Open Interest × 100 × Stock Price². We aggregate this across all strikes to see the total positioning and find key levels."
      />
    </div>

    <div style={{
      background: 'rgba(16, 185, 129, 0.1)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: '0.5rem',
      padding: '1rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <Lightbulb size={16} color="#10b981" />
        <span style={{ color: '#10b981', fontWeight: '600', fontSize: '0.85rem' }}>Key Insight</span>
      </div>
      <p style={{ margin: 0, color: '#a7f3d0', fontSize: '0.85rem', lineHeight: '1.6' }}>
        GEX is most useful for <strong>short-term trading</strong> (intraday to weekly). 
        The gamma effect is strongest on options expiration days, especially the monthly "OpEx" 
        on the third Friday.
      </p>
    </div>
  </div>
);

const LevelsTab = ({ isMobile }) => (
  <div>
    <h4 style={{ margin: '0 0 1rem 0', color: '#e2e8f0', fontSize: '1rem' }}>
      Understanding Key GEX Levels
    </h4>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Gamma Flip / HVL */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%)',
        border: '1px solid rgba(245, 158, 11, 0.4)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <Zap size={18} color="#f59e0b" />
          <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '1rem' }}>
            Gamma Flip / HVL (High Volatility Level)
          </span>
        </div>
        <p style={{ margin: '0 0 0.75rem 0', color: '#fef3c7', fontSize: '0.85rem', lineHeight: '1.6' }}>
          The most important level! This is where gamma changes from positive to negative.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: '0.75rem'
        }}>
          <div style={{ 
            background: 'rgba(16, 185, 129, 0.1)', 
            borderRadius: '0.375rem', 
            padding: '0.75rem' 
          }}>
            <div style={{ color: '#10b981', fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
              Price ABOVE Flip = Positive Gamma
            </div>
            <div style={{ color: '#a7f3d0', fontSize: '0.75rem' }}>
              Low volatility, mean-reversion, range-bound trading
            </div>
          </div>
          <div style={{ 
            background: 'rgba(239, 68, 68, 0.1)', 
            borderRadius: '0.375rem', 
            padding: '0.75rem' 
          }}>
            <div style={{ color: '#ef4444', fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
              Price BELOW Flip = Negative Gamma
            </div>
            <div style={{ color: '#fca5a5', fontSize: '0.75rem' }}>
              High volatility, trending, momentum moves
            </div>
          </div>
        </div>
      </div>

      {/* Call Walls */}
      <div style={{
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <TrendingUp size={18} color="#10b981" />
          <span style={{ color: '#10b981', fontWeight: '700', fontSize: '1rem' }}>
            Call Walls (Resistance)
          </span>
        </div>
        <p style={{ margin: 0, color: '#a7f3d0', fontSize: '0.85rem', lineHeight: '1.6' }}>
          Strikes with high positive gamma. As price rises toward these levels, market makers 
          must <strong>sell shares</strong> to stay hedged, creating selling pressure and resistance. 
          Think of them as ceilings that push price back down.
        </p>
      </div>

      {/* Put Walls */}
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <TrendingDown size={18} color="#ef4444" />
          <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '1rem' }}>
            Put Walls (Support)
          </span>
        </div>
        <p style={{ margin: 0, color: '#fca5a5', fontSize: '0.85rem', lineHeight: '1.6' }}>
          Strikes with high negative gamma. As price falls toward these levels, market makers 
          must <strong>buy shares</strong> to stay hedged, creating buying pressure and support. 
          Think of them as floors that bounce price back up.
        </p>
      </div>
    </div>
  </div>
);

const TradingTab = ({ isMobile }) => (
  <div>
    <h4 style={{ margin: '0 0 1rem 0', color: '#e2e8f0', fontSize: '1rem' }}>
      How to Trade Using GEX
    </h4>

    {/* Positive Gamma Strategies */}
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        marginBottom: '0.75rem' 
      }}>
        <Shield size={18} color="#10b981" />
        <span style={{ color: '#10b981', fontWeight: '700' }}>
          Positive Gamma Environment
        </span>
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '0.75rem'
      }}>
        {[
          { name: 'Sell Iron Condors', desc: 'Profit from range-bound behavior', icon: '🦅' },
          { name: 'Mean Reversion', desc: 'Fade moves toward GEX walls', icon: '🔄' },
          { name: 'Sell Premium', desc: 'Volatility compression benefits sellers', icon: '💰' }
        ].map((strategy, idx) => (
          <div key={idx} style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '0.5rem',
            padding: '0.75rem'
          }}>
            <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{strategy.icon}</div>
            <div style={{ color: '#10b981', fontWeight: '600', fontSize: '0.85rem' }}>{strategy.name}</div>
            <div style={{ color: '#a7f3d0', fontSize: '0.75rem' }}>{strategy.desc}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Negative Gamma Strategies */}
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        marginBottom: '0.75rem' 
      }}>
        <Flame size={18} color="#ef4444" />
        <span style={{ color: '#ef4444', fontWeight: '700' }}>
          Negative Gamma Environment
        </span>
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '0.75rem'
      }}>
        {[
          { name: 'Momentum Trades', desc: 'Trade with the trend, not against it', icon: '🚀' },
          { name: 'Buy Straddles', desc: 'Profit from volatility expansion', icon: '📈' },
          { name: 'Breakout Plays', desc: 'Watch for moves through GEX levels', icon: '💥' }
        ].map((strategy, idx) => (
          <div key={idx} style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '0.5rem',
            padding: '0.75rem'
          }}>
            <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{strategy.icon}</div>
            <div style={{ color: '#ef4444', fontWeight: '600', fontSize: '0.85rem' }}>{strategy.name}</div>
            <div style={{ color: '#fca5a5', fontSize: '0.75rem' }}>{strategy.desc}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Pro Tips */}
    <div style={{
      background: 'rgba(139, 92, 246, 0.1)',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      borderRadius: '0.5rem',
      padding: '1rem'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        marginBottom: '0.5rem' 
      }}>
        <Lightbulb size={16} color="#a78bfa" />
        <span style={{ color: '#a78bfa', fontWeight: '600' }}>Pro Tips</span>
      </div>
      <ul style={{ 
        margin: 0, 
        paddingLeft: '1.25rem', 
        color: '#c4b5fd', 
        fontSize: '0.85rem',
        lineHeight: '1.8'
      }}>
        <li>GEX levels work best on <strong>OpEx days</strong> - especially monthly OpEx (3rd Friday)</li>
        <li>Combine GEX with <strong>volume and price action</strong> for confirmation</li>
        <li>Watch for <strong>regime changes</strong> at the gamma flip level</li>
        <li>Large GEX = stronger "magnetic" effect on price</li>
      </ul>
    </div>
  </div>
);

const GlossaryTab = ({ isMobile }) => {
  const terms = [
    { term: 'GEX', def: 'Gamma Exposure - measures the dollar amount of shares MMs must trade per $1 move in the underlying' },
    { term: 'Gamma', def: 'Rate of change of delta. How much an options delta changes when the stock moves $1' },
    { term: 'Delta', def: 'How much an options price changes when the stock moves $1. Also represents hedge ratio' },
    { term: 'HVL', def: 'High Volatility Level - same as gamma flip. The price where gamma transitions from positive to negative' },
    { term: 'Call Wall', def: 'Strike with highest positive GEX. Acts as resistance as MMs sell shares approaching it' },
    { term: 'Put Wall', def: 'Strike with highest negative GEX. Acts as support as MMs buy shares approaching it' },
    { term: 'Positive Gamma', def: 'Price is above HVL. MMs hedge against price moves, dampening volatility' },
    { term: 'Negative Gamma', def: 'Price is below HVL. MMs hedge with price moves, amplifying volatility' },
    { term: 'OpEx', def: 'Options Expiration day. Gamma effects are strongest on these days' },
    { term: 'Pinning', def: 'Price tendency to settle near high-OI strikes on OpEx as gamma decays' },
    { term: 'Max Pain', def: 'Strike price where options sellers profit most / buyers lose most at expiration' },
    { term: 'Open Interest', def: 'Total number of outstanding options contracts at a given strike' }
  ];

  return (
    <div>
      <h4 style={{ margin: '0 0 1rem 0', color: '#e2e8f0', fontSize: '1rem' }}>
        GEX Glossary
      </h4>
      
      <div style={{
        display: 'grid',
        gap: '0.5rem',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        {terms.map((item, idx) => (
          <div key={idx} style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(71, 85, 105, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.75rem'
          }}>
            <div style={{ 
              color: '#60a5fa', 
              fontWeight: '700', 
              fontSize: '0.9rem',
              marginBottom: '0.25rem'
            }}>
              {item.term}
            </div>
            <div style={{ 
              color: '#94a3b8', 
              fontSize: '0.8rem',
              lineHeight: '1.5'
            }}>
              {item.def}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EducationCard = ({ title, content, icon }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid rgba(71, 85, 105, 0.3)',
    borderRadius: '0.5rem',
    padding: '1rem'
  }}>
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem', 
      marginBottom: '0.5rem' 
    }}>
      <span style={{ fontSize: '1.25rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>
        {title}
      </h4>
    </div>
    <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.6' }}>
      {content}
    </p>
  </div>
);

// ============================================
// METRIC CARD - Responsive with tooltips
// ============================================
const MetricCard = ({ label, value, icon, color, tooltip, badge, highlight, isMobile }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const ref = useRef(null);

  return (
    <div 
      ref={ref}
      style={{
        background: highlight 
          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.1) 100%)'
          : 'rgba(30, 41, 59, 0.5)',
        border: `1px solid ${highlight ? 'rgba(245, 158, 11, 0.4)' : 'rgba(71, 85, 105, 0.5)'}`,
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem',
        position: 'relative'
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'start',
        marginBottom: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
          <span style={{ color }}>{icon}</span>
          <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', fontWeight: '500' }}>{label}</span>
        </div>
        
        <div
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(!showTooltip);
          }}
          onMouseEnter={() => !isMobile && setShowTooltip(true)}
          onMouseLeave={() => !isMobile && setShowTooltip(false)}
          style={{ cursor: 'pointer', position: 'relative' }}
        >
          <Info size={14} color="#64748b" />
          
          {showTooltip && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: '0.5rem',
              padding: '0.75rem',
              background: '#1e293b',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              fontSize: '0.75rem',
              color: '#cbd5e1',
              width: isMobile ? '180px' : '220px',
              zIndex: 100,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
              lineHeight: '1.5'
            }}>
              {tooltip}
            </div>
          )}
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ 
          fontSize: isMobile ? '1.125rem' : '1.5rem', 
          fontWeight: '700', 
          color 
        }}>
          {value}
        </span>
        
        {badge && (
          <span style={{
            padding: '0.125rem 0.5rem',
            background: `${color}20`,
            borderRadius: '1rem',
            fontSize: '0.6rem',
            fontWeight: '600',
            color
          }}>
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================
// WALL CARD - Responsive
// ============================================
const WallCard = ({ type, wall, currentPrice, allWalls, isMobile }) => {
  const isCall = type === 'call';
  const color = isCall ? '#10b981' : '#ef4444';
  const bgColor = isCall ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';
  const borderColor = isCall ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)';
  
  const distanceToWall = wall ? ((wall.strike - currentPrice) / currentPrice * 100) : 0;
  
  return (
    <div style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '0.75rem',
      padding: isMobile ? '1rem' : '1.25rem'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'start',
        marginBottom: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isCall ? <TrendingUp size={20} color={color} /> : <TrendingDown size={20} color={color} />}
          <span style={{ 
            fontSize: isMobile ? '0.85rem' : '0.9rem', 
            fontWeight: '600', 
            color 
          }}>
            Top {isCall ? 'Call' : 'Put'} Wall ({isCall ? 'Resistance' : 'Support'})
          </span>
        </div>
        
        {wall && (
          <span style={{
            padding: '0.25rem 0.5rem',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '0.25rem',
            fontSize: '0.7rem',
            color: '#94a3b8'
          }}>
            {distanceToWall > 0 ? '+' : ''}{distanceToWall.toFixed(1)}% away
          </span>
        )}
      </div>
      
      {wall ? (
        <>
          <div style={{ 
            fontSize: isMobile ? '1.75rem' : '2rem', 
            fontWeight: '700', 
            color,
            marginBottom: '0.5rem'
          }}>
            ${wall.strike.toFixed(2)}
          </div>
          
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '1rem' }}>
            GEX: {formatGEX(wall.gex)} | OI: {wall.openInterest?.toLocaleString() || 'N/A'}
          </div>
          
          {allWalls && allWalls.length > 1 && (
            <div>
              <div style={{ 
                fontSize: '0.7rem', 
                color: '#64748b', 
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Other {isCall ? 'Resistance' : 'Support'} Levels
              </div>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {allWalls.slice(1, 5).map((w, idx) => (
                  <span
                    key={idx}
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid rgba(71, 85, 105, 0.3)',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      color: '#cbd5e1'
                    }}
                  >
                    ${w.strike.toFixed(0)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
          No significant {isCall ? 'call' : 'put'} wall detected
        </div>
      )}
    </div>
  );
};

// ============================================
// GEX PROFILE CHART - Interactive with hover (NaN-safe)
// ============================================
const GEXProfileChart = ({ strikes, currentPrice, flipLevel, isMobile, hoveredBar, setHoveredBar }) => {
  const chartRef = useRef(null);
  
  // Guard against invalid data - show loading state instead of crashing
  if (!strikes || strikes.length === 0 || !currentPrice || isNaN(currentPrice)) {
    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid rgba(71, 85, 105, 0.5)',
        borderRadius: '0.75rem',
        padding: isMobile ? '1rem' : '1.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <BarChart3 size={24} color="#a78bfa" style={{ marginBottom: '0.5rem' }} />
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem' }}>Loading GEX profile data...</p>
      </div>
    );
  }
  
  const sortedStrikes = [...strikes].sort((a, b) => a.strike - b.strike);
  // Ensure non-zero maxGEX to prevent division by zero
  const maxGEX = Math.max(...sortedStrikes.map(s => Math.abs(s.gex || 0))) || 1;
  
  const lowerBound = currentPrice * 0.85;
  const upperBound = currentPrice * 1.15;
  const filteredStrikes = sortedStrikes.filter(s => s.strike >= lowerBound && s.strike <= upperBound && s.gex !== undefined);
  
  // Handle empty filtered strikes
  if (filteredStrikes.length === 0) {
    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid rgba(71, 85, 105, 0.5)',
        borderRadius: '0.75rem',
        padding: isMobile ? '1rem' : '1.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <BarChart3 size={24} color="#64748b" style={{ marginBottom: '0.5rem' }} />
        <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>No strikes in current price range</p>
      </div>
    );
  }
  
  const handleBarInteraction = (strike, event) => {
    if (isMobile) {
      setHoveredBar(hoveredBar?.strike === strike.strike ? null : strike);
    } else {
      setHoveredBar(strike);
    }
  };

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)',
      border: '1px solid rgba(71, 85, 105, 0.5)',
      borderRadius: '0.75rem',
      padding: isMobile ? '1rem' : '1.5rem',
      marginBottom: '1.5rem'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <h4 style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.9rem' : '1rem', 
          fontWeight: '600',
          color: '#e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <BarChart3 size={20} color="#a78bfa" />
          GEX Profile by Strike
        </h4>
        
        {isMobile && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.375rem',
            padding: '0.25rem 0.5rem',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '0.25rem',
            fontSize: '0.65rem',
            color: '#93c5fd'
          }}>
            <MousePointer size={12} />
            Tap bars for details
          </div>
        )}
      </div>
      
      {/* Legend */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: isMobile ? '0.75rem' : '1.5rem', 
        marginBottom: '1rem',
        fontSize: '0.7rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{ width: '12px', height: '12px', background: '#10b981', borderRadius: '2px' }} />
          <span style={{ color: '#94a3b8' }}>Call GEX (Resistance)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{ width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px' }} />
          <span style={{ color: '#94a3b8' }}>Put GEX (Support)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{ width: '2px', height: '12px', background: '#60a5fa' }} />
          <span style={{ color: '#94a3b8' }}>Current Price</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{ 
            width: '12px', 
            height: '2px', 
            background: '#f59e0b', 
            borderTop: '2px dashed #f59e0b' 
          }} />
          <span style={{ color: '#94a3b8' }}>Gamma Flip</span>
        </div>
      </div>
      
      {/* Hover tooltip */}
      {hoveredBar && (
        <div style={{
          background: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(139, 92, 246, 0.5)',
          borderRadius: '0.5rem',
          padding: '0.75rem',
          marginBottom: '0.75rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <div>
            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Strike: </span>
            <span style={{ color: '#e2e8f0', fontWeight: '700' }}>${hoveredBar.strike.toFixed(0)}</span>
          </div>
          <div>
            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>GEX: </span>
            <span style={{ 
              color: hoveredBar.gex > 0 ? '#10b981' : '#ef4444', 
              fontWeight: '700' 
            }}>
              {formatGEX(hoveredBar.gex)}
            </span>
          </div>
          <div>
            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>OI: </span>
            <span style={{ color: '#e2e8f0', fontWeight: '700' }}>
              {hoveredBar.openInterest?.toLocaleString() || 'N/A'}
            </span>
          </div>
          <div>
            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Type: </span>
            <span style={{ 
              color: hoveredBar.gex > 0 ? '#10b981' : '#ef4444', 
              fontWeight: '600' 
            }}>
              {hoveredBar.gex > 0 ? 'Resistance' : 'Support'}
            </span>
          </div>
        </div>
      )}
      
      {/* Chart */}
      <div 
        ref={chartRef}
        style={{ 
          position: 'relative',
          height: isMobile ? '200px' : '250px',
          background: 'rgba(15, 23, 42, 0.5)',
          borderRadius: '0.5rem',
          padding: '1rem 0.5rem',
          overflow: 'hidden'
        }}
      >
        {/* Y-axis labels */}
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          bottom: '0',
          width: '40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '0.5rem 0',
          fontSize: '0.6rem',
          color: '#64748b'
        }}>
          <span style={{ color: '#10b981' }}>+{formatGEX(maxGEX || 0)}</span>
          <span>0</span>
          <span style={{ color: '#ef4444' }}>-{formatGEX(maxGEX || 0)}</span>
        </div>

        {/* Zero line */}
        <div style={{
          position: 'absolute',
          left: '45px',
          right: '10px',
          top: '50%',
          height: '1px',
          background: 'rgba(100, 116, 139, 0.3)'
        }} />
        
        {/* Bars */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          height: '100%',
          paddingLeft: '45px',
          paddingRight: '10px'
        }}>
          {filteredStrikes.map((strike, idx) => {
            // Safe calculations with guards against NaN
            const gexValue = strike.gex || 0;
            const barHeight = maxGEX > 0 ? Math.min(100, Math.abs(gexValue) / maxGEX * 100) : 0;
            const isPositive = gexValue > 0;
            const isCurrentPrice = currentPrice && Math.abs(strike.strike - currentPrice) < currentPrice * 0.01;
            const isFlipLevel = flipLevel && currentPrice && Math.abs(strike.strike - flipLevel) < currentPrice * 0.01;
            const isHovered = hoveredBar?.strike === strike.strike;
            
            return (
              <div
                key={idx}
                onClick={() => handleBarInteraction(strike)}
                onMouseEnter={() => !isMobile && setHoveredBar(strike)}
                onMouseLeave={() => !isMobile && setHoveredBar(null)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  flex: 1,
                  maxWidth: isMobile ? '15px' : '40px',
                  position: 'relative',
                  cursor: 'pointer'
                }}
              >
                {/* Bar */}
                <div style={{
                  position: 'absolute',
                  bottom: isPositive ? '50%' : 'auto',
                  top: isPositive ? 'auto' : '50%',
                  width: isMobile ? '60%' : '80%',
                  height: `${barHeight / 2}%`,
                  background: isPositive 
                    ? `linear-gradient(180deg, ${isHovered ? '#34d399' : '#10b981'} 0%, ${isHovered ? '#10b981' : '#059669'} 100%)`
                    : `linear-gradient(0deg, ${isHovered ? '#f87171' : '#ef4444'} 0%, ${isHovered ? '#ef4444' : '#dc2626'} 100%)`,
                  borderRadius: isPositive ? '4px 4px 0 0' : '0 0 4px 4px',
                  transition: 'all 0.2s ease',
                  transform: isHovered ? 'scaleX(1.2)' : 'scaleX(1)',
                  boxShadow: isHovered ? `0 0 10px ${isPositive ? '#10b981' : '#ef4444'}50` : 'none'
                }} />
                
                {/* Current price marker */}
                {isCurrentPrice && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    background: '#60a5fa',
                    zIndex: 10
                  }} />
                )}
                
                {/* Flip level marker */}
                {isFlipLevel && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    background: 'repeating-linear-gradient(to bottom, #f59e0b 0, #f59e0b 4px, transparent 4px, transparent 8px)',
                    zIndex: 10
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================
// AI ANALYSIS SECTION
// ============================================
const AIAnalysisSection = ({ analysis, loading, symbol, metrics, gexData, isMobile }) => {
  const displayAnalysis = analysis || generateFallbackAnalysis(metrics, gexData, symbol);
  
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      borderRadius: '0.75rem',
      padding: isMobile ? '1rem' : '1.5rem',
      marginBottom: '1.5rem'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: '0.75rem', 
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '36px',
            height: '36px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Brain size={20} color="white" />
          </div>
          <h4 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: '600', color: '#e2e8f0' }}>
            AI Gamma Analysis
          </h4>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{
            padding: '0.375rem 0.75rem',
            background: displayAnalysis.sentiment === 'Bullish' 
              ? 'rgba(16, 185, 129, 0.2)' 
              : displayAnalysis.sentiment === 'Bearish' 
                ? 'rgba(239, 68, 68, 0.2)' 
                : 'rgba(251, 191, 36, 0.2)',
            border: `1px solid ${displayAnalysis.sentiment === 'Bullish' ? '#10b981' : displayAnalysis.sentiment === 'Bearish' ? '#ef4444' : '#fbbf24'}40`,
            borderRadius: '2rem',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: displayAnalysis.sentiment === 'Bullish' ? '#10b981' : displayAnalysis.sentiment === 'Bearish' ? '#ef4444' : '#fbbf24'
          }}>
            {displayAnalysis.sentiment}
          </span>
          
          <span style={{
            padding: '0.375rem 0.75rem',
            background: 'rgba(139, 92, 246, 0.2)',
            border: '1px solid rgba(139, 92, 246, 0.4)',
            borderRadius: '2rem',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#a78bfa'
          }}>
            {displayAnalysis.confidence} Confidence
          </span>
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader size={32} color="#a78bfa" className="spin" />
          <p style={{ color: '#a78bfa', marginTop: '0.5rem' }}>Analyzing gamma exposure...</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '1rem'
        }}>
          {/* Key Observations */}
          <div style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '0.5rem',
            padding: '1rem'
          }}>
            <h5 style={{ 
              margin: '0 0 0.75rem 0', 
              fontSize: '0.85rem', 
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Key Observations
            </h5>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '1.25rem', 
              fontSize: '0.8rem', 
              color: '#cbd5e1',
              lineHeight: '1.8'
            }}>
              {displayAnalysis.keyPoints?.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>
          
          {/* Trading Bias */}
          <div style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '0.5rem',
            padding: '1rem'
          }}>
            <h5 style={{ 
              margin: '0 0 0.5rem 0', 
              fontSize: '0.85rem', 
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Suggested Trading Approach
            </h5>
            <div style={{ 
              fontSize: '1rem', 
              fontWeight: '600',
              color: '#e2e8f0',
              marginBottom: '0.5rem'
            }}>
              {displayAnalysis.tradingBias}
            </div>
            <div style={{ 
              fontSize: '0.8rem', 
              color: '#64748b'
            }}>
              Volatility Regime: <span style={{ 
                color: displayAnalysis.volatilityRegime === 'LOW' ? '#10b981' : '#ef4444',
                fontWeight: '600'
              }}>{displayAnalysis.volatilityRegime}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// TRADING IMPLICATIONS
// ============================================
const TradingImplications = ({ metrics, gexData, isMobile }) => {
  if (!metrics || !gexData) return null;
  
  const { isPositiveGamma, topCallWall, topPutWall } = metrics;
  
  const strategies = isPositiveGamma ? [
    { name: 'Iron Condor', description: 'Sell premium between put and call walls', risk: 'Low', reward: 'Limited', icon: '🦅' },
    { name: 'Sell Straddles/Strangles', description: 'Profit from volatility compression', risk: 'Medium', reward: 'Limited', icon: '📉' },
    { name: 'Mean Reversion Scalps', description: 'Fade moves toward GEX walls', risk: 'Low', reward: 'Limited', icon: '🔄' }
  ] : [
    { name: 'Momentum Breakouts', description: 'Trade with the trend, not against it', risk: 'Medium', reward: 'High', icon: '🚀' },
    { name: 'Long Straddles/Strangles', description: 'Profit from volatility expansion', risk: 'Medium', reward: 'Unlimited', icon: '📈' },
    { name: 'Directional Plays', description: 'Use debit spreads for defined risk', risk: 'Defined', reward: 'High', icon: '🎯' }
  ];
  
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)',
      border: '1px solid rgba(71, 85, 105, 0.5)',
      borderRadius: '0.75rem',
      padding: isMobile ? '1rem' : '1.5rem'
    }}>
      <h4 style={{ 
        margin: '0 0 1rem 0', 
        fontSize: isMobile ? '0.9rem' : '1rem', 
        fontWeight: '600',
        color: '#e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Target size={20} color="#f59e0b" />
        Suggested Strategies for {isPositiveGamma ? 'Positive' : 'Negative'} Gamma
      </h4>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '1rem'
      }}>
        {strategies.map((strategy, idx) => (
          <div
            key={idx}
            style={{
              background: 'rgba(15, 23, 42, 0.5)',
              border: '1px solid rgba(100, 116, 139, 0.2)',
              borderRadius: '0.5rem',
              padding: '1rem'
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>{strategy.icon}</span>
              <span style={{ fontWeight: '600', color: '#e2e8f0', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
                {strategy.name}
              </span>
            </div>
            <p style={{ 
              margin: '0 0 0.5rem 0', 
              fontSize: '0.75rem', 
              color: '#94a3b8' 
            }}>
              {strategy.description}
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem',
              fontSize: '0.65rem',
              flexWrap: 'wrap'
            }}>
              <span style={{
                padding: '0.125rem 0.375rem',
                background: strategy.risk === 'Low' ? 'rgba(16, 185, 129, 0.2)' : strategy.risk === 'High' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(251, 191, 36, 0.2)',
                borderRadius: '0.25rem',
                color: strategy.risk === 'Low' ? '#10b981' : strategy.risk === 'High' ? '#ef4444' : '#fbbf24'
              }}>
                Risk: {strategy.risk}
              </span>
              <span style={{
                padding: '0.125rem 0.375rem',
                background: 'rgba(96, 165, 250, 0.2)',
                borderRadius: '0.25rem',
                color: '#60a5fa'
              }}>
                Reward: {strategy.reward}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Key Levels Summary */}
      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        background: 'rgba(15, 23, 42, 0.5)',
        borderRadius: '0.5rem',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '1rem'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>
            KEY RESISTANCE (CALL WALL)
          </div>
          <div style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: '700', color: '#10b981' }}>
            {topCallWall ? `$${topCallWall.strike.toFixed(2)}` : 'N/A'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>
            KEY SUPPORT (PUT WALL)
          </div>
          <div style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: '700', color: '#ef4444' }}>
            {topPutWall ? `$${topPutWall.strike.toFixed(2)}` : 'N/A'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem' }}>
            GAMMA FLIP LEVEL
          </div>
          <div style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: '700', color: '#f59e0b' }}>
            ${gexData.flipLevel?.toFixed(2) || 'N/A'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================
function formatGEX(value) {
  if (!value) return '0';
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (absValue >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (absValue >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
}

function generateFallbackAnalysis(metrics, gexData, symbol) {
  if (!metrics || !gexData) {
    return {
      sentiment: 'Neutral',
      confidence: 'Low',
      keyPoints: ['Loading analysis...'],
      tradingBias: 'Wait for data',
      volatilityRegime: 'UNKNOWN'
    };
  }

  const { isPositiveGamma, distanceToFlip, gexRatio, topCallWall, topPutWall } = metrics;
  const sentiment = isPositiveGamma 
    ? (gexRatio > 1.5 ? 'Bullish' : 'Strongly Range-Bound')
    : (gexRatio < 0.5 ? 'Bearish' : 'Volatile');
  
  return {
    sentiment,
    confidence: Math.abs(distanceToFlip) > 5 ? 'High' : 'Medium',
    keyPoints: [
      `Price is ${Math.abs(distanceToFlip).toFixed(1)}% ${isPositiveGamma ? 'ABOVE' : 'BELOW'} the High Volatility Level (gamma flip)`,
      `Call/Put GEX ratio of ${gexRatio.toFixed(2)} indicates ${gexRatio > 1 ? 'bullish' : 'bearish'} positioning`,
      topCallWall ? `Major resistance at $${topCallWall.strike.toFixed(0)} (Call Wall)` : 'No significant call wall detected',
      topPutWall ? `Major support at $${topPutWall.strike.toFixed(0)} (Put Wall)` : 'No significant put wall detected'
    ],
    tradingBias: isPositiveGamma 
      ? 'Sell Premium / Mean Reversion' 
      : 'Buy Premium / Momentum Trades',
    volatilityRegime: isPositiveGamma ? 'LOW' : 'HIGH'
  };
}

// Add CSS for spin animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

export default GammaExposureDashboard;